import { useEffect, useState } from "react";
import { ChatMessage, ChatMessageDeletedEvent } from "@azure/communication-chat";
import { StatefulChatClient } from "@azure/communication-react";
import { ChatMessageEditedEvent, ChatMessageReceivedEvent } from "@azure/communication-signaling";
import { convertEventToChatMessage } from "../utils";

type ChatMessageWithThreadId = ChatMessage & { threadId: string };
const convertEventToChatMessageWithThreadId = (
    event: ChatMessageReceivedEvent | ChatMessageEditedEvent | ChatMessageDeletedEvent
): ChatMessageWithThreadId => {
    const message = convertEventToChatMessage(event);
    return { ...message, threadId: event.threadId };
};

/**
 * Used by the CallWithChatComposite to track unread messages for showing as a badge on the Chat Button.
 * @private
 */
export const useUnreadMessagesTracker = (
    chatClient: StatefulChatClient,
    isChatPaneVisible: boolean,
    isChatInitiazed: boolean,
    forThreadId?: string
): number => {
    // Store messageIds of unread messages
    const [unreadChatMessages, setUnreadChatMessages] = useState<Set<string>>(new Set());

    useEffect(() => {
        // reset when the thread id changes => chat got cleared
        setUnreadChatMessages(new Set());
    }, [forThreadId]);

    useEffect(() => {
        // Clear unread messages when chat pane is opened
        if (isChatPaneVisible || !isChatInitiazed) {
            setUnreadChatMessages(new Set());
            return;
        }

        // Increment unread messages when a new message is received and the chat pane is closed
        const incrementUnreadChatMessagesCount = (event: ChatMessageReceivedEvent): void => {
            // event.threadId     ==> group counts by thread id
            const message = convertEventToChatMessageWithThreadId(event);

            if (!isChatPaneVisible && validNewChatMessage(message) && isChatMessageRelatedToThread(message, forThreadId)) {
                setUnreadChatMessages((prevUnreadChatMessages) => {
                    const newUnreadChatMessages = new Set(prevUnreadChatMessages);
                    newUnreadChatMessages.add(message.id);
                    return newUnreadChatMessages;
                });
            }
        };

        // Decrement unread messages when a message is deleted and the chat pane is closed
        const decrementUnreadChatMessagesCount = (event: ChatMessageDeletedEvent): void => {
            const message = convertEventToChatMessageWithThreadId(event);
            if (!isChatPaneVisible) {
                setUnreadChatMessages((prevUnreadChatMessages) => {
                    const newUnreadChatMessages = new Set(prevUnreadChatMessages);
                    newUnreadChatMessages.delete(message.id);
                    return newUnreadChatMessages;
                });
            }
        };

        chatClient.on('chatMessageReceived', incrementUnreadChatMessagesCount);
        chatClient.on('chatMessageDeleted', decrementUnreadChatMessagesCount);

        return () => {
            chatClient.off('chatMessageReceived', incrementUnreadChatMessagesCount);
            chatClient.off('chatMessageDeleted', decrementUnreadChatMessagesCount);
        };
    }, [chatClient, setUnreadChatMessages, isChatPaneVisible, isChatInitiazed, forThreadId]);

    return unreadChatMessages.size;
};

/**
 * Helper function to determine if the message in the event is a valid one from a user.
 * Display name is used since system messages will not have one.
 */
const validNewChatMessage = (message: ChatMessage): boolean =>
    !!message.senderDisplayName && (message.type === 'text' || message.type === 'html');


const isChatMessageRelatedToThread = (message: ChatMessageWithThreadId, threadId?: string)  =>
    !threadId || message.threadId === threadId
